import { Box, Typography } from '@mui/material';
import {
  Chart,
  TimeScale,
  LinearScale,
  CategoryScale,
  BarElement,
  Tooltip,
} from 'chart.js';
import dayjs, { Dayjs } from 'dayjs';
import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';

import 'chartjs-adapter-dayjs-3';

Chart.register(BarElement, TimeScale, LinearScale, CategoryScale, Tooltip);

type BarChartProps = React.ComponentProps<typeof Bar>;

interface EstateActualPriceHistoryChartProps {
  prices: {
    date: string;
    actualPrice: number;
  }[];
}

export function EstateActualPriceHistoryChart({
  prices,
}: EstateActualPriceHistoryChartProps) {
  const { data, options } = useMemo<{
    data: BarChartProps['data'];
    options: BarChartProps['options'];
  }>(() => {
    const enumerateDaysBetweenDates = (startDate: Dayjs, endDate: Dayjs) => {
      let current = startDate;
      const dates: Date[] = [];

      while (current.isSame(endDate) || current.isBefore(endDate)) {
        dates.push(current.toDate());
        current = current.add(1, 'days');
      }
      return dates;
    };

    const sortedPrices = [...prices].sort(
      (a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf(),
    );
    const start = sortedPrices.length && sortedPrices[0].date;
    const end =
      sortedPrices.length && sortedPrices[sortedPrices.length - 1].date;

    const reversed = [...sortedPrices].reverse();
    const filledPrices = sortedPrices.length
      ? enumerateDaysBetweenDates(
          dayjs(start).add(-7, 'days'),
          dayjs(end).add(7, 'days'),
        ).map((x) => {
          const price = reversed.find((y) => x >= dayjs(y.date).toDate());
          return {
            date: x.toISOString(),
            // eslint-disable-next-line no-nested-ternary
            price: price
              ? price.actualPrice
              : x < dayjs(sortedPrices[0].date).toDate()
              ? sortedPrices[0].actualPrice
              : reversed[0].actualPrice,
          };
        })
      : [];

    const minPrice = Math.min(...sortedPrices.map((x) => x.actualPrice));
    const maxPrice = Math.max(...sortedPrices.map((x) => x.actualPrice));
    const delta = maxPrice - minPrice;

    const getStepSize = () => {
      if (delta > 2000000) {
        return 500000;
      }
      if (delta > 1500000) {
        return 400000;
      }
      if (delta > 1000000) {
        return 300000;
      }
      if (delta > 500000) {
        return 200000;
      }
      return 100000;
    };

    return {
      data: {
        datasets: [
          {
            data: filledPrices.map((x) => ({ x: x.date, y: x.price })),
            backgroundColor: 'rgba(0, 0, 255, 0.3)',
            borderWidth: 0,
            barThickness: 'flex',
            borderSkipped: true,
            barPercentage: 1,
            categoryPercentage: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            type: 'time',
            time: {
              unit: 'day',
            },
          },
          y: {
            suggestedMin: 0,
            max: maxPrice + maxPrice / 5,
            ticks: {
              stepSize: getStepSize(),
            },
          },
        },
        plugins: {
          tooltip: {
            enabled: true,
            displayColors: false,
            callbacks: {
              title([context]) {
                // @ts-ignore
                if (!context.raw) {
                  return;
                }
                // @ts-ignore
                return `${dayjs(new Date(context.raw.x)).format('DD.MM.YYYY')}`;
              },
              label(context) {
                // @ts-ignore
                if (!context.raw) {
                  return;
                }
                // @ts-ignore
                return `${context.raw.y.toLocaleString()}₽`;
              },
            },
          },
        },
      },
    };
  }, [prices]);

  if (prices.length < 2) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 8,
          minHeight: 200,
        }}
      >
        <Typography sx={{ textAlign: 'center' }} variant="body1">
          Недостаточно данных для построения графика
        </Typography>
      </Box>
    );
  }

  return <Bar data={data} options={options} />;
}
